import { _uniq, _zip } from 'underscore-es';

const isObject = value =>
  value && typeof value === 'object' && !Array.isArray(value);

const mergeProperties = (key, a, b) => {
  const existsInA = Object.prototype.hasOwnProperty.call(a, key);
  const existsInB = Object.prototype.hasOwnProperty.call(b, key);

  // If property is undefined in b, remove it (return undefined)
  if (existsInB && b[key] === undefined) {
    return undefined;
  }

  if (!existsInA) {
    return b[key];
  }

  if (!existsInB) {
    return a[key];
  }

  const aValue = a[key];
  const bValue = b[key];

  // Handle null/undefined cases (but not if bValue is undefined, we already handled that)
  if (aValue == null) return bValue;

  // Handle arrays
  const bothArrays = Array.isArray(aValue) && Array.isArray(bValue);
  if (bothArrays) return mergeArrays(aValue, bValue);

  // Handle mismatched types
  if (Array.isArray(aValue) !== Array.isArray(bValue)) {
    return bValue;
  }

  // Handle objects
  if (isObject(aValue) && isObject(bValue)) {
    return mergeDeep(aValue, bValue);
  }

  // Default to bValue for primitives
  return bValue;
};

const mergeObjects = (a, b) => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  const keys = _uniq([...aKeys, ...bKeys]);
  const values = keys.map(key => {
    const result = mergeProperties(key, a, b);
    if (Array.isArray(result)) {
      return result.filter(item => item !== undefined);
    }
    return result;
  });

  // Filter out keys where the value is undefined
  const mergedEntries = _zip(keys, values).filter(
    ([_, value]) => value !== undefined
  );

  return Object.fromEntries(mergedEntries);
};

const mergeArrays = (a, b) => {
  return Array.from({ length: Math.max(a.length, b.length) }, (_, i) => {
    const aItem = i < a.length ? a[i] : undefined;
    const bItem = i < b.length ? b[i] : undefined;

    if (aItem !== undefined && bItem !== undefined) {
      if (isObject(aItem) && isObject(bItem)) {
        return mergeDeep(aItem, bItem);
      }
      return bItem;
    }
    // return bItem by default
    return bItem;
  });
};

const mergeDeep = (...objects) => {
  if (objects.length === 0) return {};
  if (objects.length === 1) return objects[0];
  const [a, b, ...others] = objects;
  const merged = mergeObjects(a, b);

  if (others.length === 0) {
    return merged;
  } else {
    return mergeDeep(merged, ...others);
  }
};

export default mergeDeep;
